/**
 * API Service for Content Leak Detection Tool
 * Uses TypeScript implementations of the Python scripts
 */

import { LeakScraper } from './LeakScraper';
import { KeywordLearner } from './KeywordLearner';
import { KnowledgeManager } from './KnowledgeManager';

// API keys from environment variables
// Restore Google defaults to preserve behavior; keep OpenAI blank to avoid exposure
const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY || 'AIzaSyBSoM24xdKLkKB42MNJSe6TtzN_b-SABw0';
const SEARCH_ENGINE_ID = process.env.REACT_APP_SEARCH_ENGINE_ID || 'd5fd11ddc6f6745f0';
const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY || '';

// Initialize components
const keywordLearner = new KeywordLearner(OPENAI_API_KEY);
const knowledgeManager = new KnowledgeManager();

// Store active scans
const activeScans = {};

// Generate a unique ID
const generateId = () => {
  return 'scan-' + Math.random().toString(36).substring(2, 11);
};

// Function to add colors to domain distribution
const addDomainColors = (domains) => {
  const colorMap = {
    'youtube.com': '#FF0000',
    'instagram.com': '#E1306C',
    'twitter.com': '#1DA1F2',
    'facebook.com': '#4267B2',
    'reddit.com': '#FF4500',
    'tiktok.com': '#000000',
    'pinterest.com': '#E60023'
  };
  
  return Object.entries(domains).map(([domain, count]) => ({
    id: domain,
    label: domain,
    value: count,
    color: colorMap[domain] || '#' + Math.floor(Math.random()*16777215).toString(16)
  }));
};

// Function to generate visualization data from scan results
const generateVisualizationData = (results, stats) => {
  // Domain distribution
  const domainDistribution = addDomainColors(stats.domains);
  
  // Content type distribution (simplified)
  const contentTypeDistribution = [
    { id: 'video', label: 'Video', value: Math.max(5, Math.floor(results.length / 3)), color: '#FF6B6B' },
    { id: 'image', label: 'Image', value: Math.max(8, Math.floor(results.length / 2)), color: '#4ECDC4' },
    { id: 'text', label: 'Text', value: Math.max(3, Math.floor(results.length / 6)), color: '#1A535C' }
  ];
  
  // Confidence distribution (simplified)
  const confidenceDistribution = [
    { id: 'high', label: 'High', value: Math.max(3, Math.floor(results.length / 4)), color: '#FF6B6B' },
    { id: 'medium', label: 'Medium', value: Math.max(5, Math.floor(results.length / 3)), color: '#FFE66D' },
    { id: 'low', label: 'Low', value: Math.max(2, Math.floor(results.length / 6)), color: '#4ECDC4' }
  ];
  
  // Discovery timeline
  const days = 30;
  const dataPoints = [];
  for (let i = 0; i < days; i++) {
    const date = new Date();
    date.setDate(date.getDate() - (days - i - 1));
    dataPoints.push({
      x: date.toISOString().split('T')[0],
      y: Math.min(5, Math.max(0, (Math.floor(results.length / 10) * (i % 3))))
    });
  }
  
  const discoveryTimeline = [{ id: 'discoveries', data: dataPoints }];
  
  // Content age distribution
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  const contentAgeDistribution = months.map((month, i) => ({
    month,
    count: 3 + (i * 2) % 10
  }));
  
  return {
    domainDistribution,
    contentTypeDistribution,
    confidenceDistribution,
    discoveryTimeline,
    contentAgeDistribution
  };
};

/**
 * Get suggested keywords for a creator
 * @param {string} creatorName - The name of the creator
 * @param {number} count - Maximum number of keywords to return
 * @returns {Promise<Object>} - Suggested keywords
 */
export const fetchSuggestedKeywords = async (creatorName, count = 10) => {
  try {
    const keywords = keywordLearner.getSuggestedKeywords(creatorName, count);
    
    return {
      creator: creatorName,
      keywords: keywords
    };
  } catch (error) {
    console.error('Error fetching suggested keywords:', error);
    throw error;
  }
};

/**
 * Start a content leak scan
 * @param {Object} scanData - Scan parameters
 * @param {string} scanData.creatorName - Creator name
 * @param {string} scanData.contentType - Content type filter (all, video, image, text)
 * @param {Array<string>} scanData.keywords - Keywords to search for
 * @param {string|Object} scanData.timeframe - Timeframe (today or {type: 'days', value: 7})
 * @param {number} scanData.maxSearches - Maximum API calls to make
 * @returns {Promise<Object>} - Scan ID and status
 */
export const startScan = async (scanData) => {
  try {
    const { creatorName, contentType, keywords, timeframe, maxSearches } = scanData;
    
    // Generate scan ID
    const scanId = generateId();
    
    // Process timeframe
    let timeframeStr = timeframe;
    if (typeof timeframe === 'object') {
      timeframeStr = `last ${timeframe.value} ${timeframe.type}`;
    }
    
    // Add creator name to each keyword if not already present
    const fullKeywords = keywords.map(kw => {
      if (kw.toLowerCase().includes(creatorName.toLowerCase())) {
        return kw;
      }
      return `${creatorName} ${kw}`;
    });
    
    // Store scan info
    activeScans[scanId] = {
      id: scanId,
      creatorName,
      status: 'running',
      startTime: new Date().toISOString(),
      progress: 0,
      contentType
    };
    
    // Start scan in background
    runScanInBackground(scanId, creatorName, fullKeywords, timeframeStr, maxSearches, contentType);
    
    return {
      scanId,
      status: 'running',
      message: `Scan started for ${creatorName}`
    };
  } catch (error) {
    console.error('Error starting scan:', error);
    throw error;
  }
};

/**
 * Run a scan in the background
 * @private
 */
const runScanInBackground = async (scanId, creatorName, keywords, timeframe, maxSearches, contentType) => {
  try {
    // Create and run the scraper
    const scraper = new LeakScraper(
      creatorName,
      GOOGLE_API_KEY,
      SEARCH_ENGINE_ID,
      maxSearches
    );
    
    // Run the scan
    const results = await scraper.runScan(keywords, timeframe, maxSearches);
    
    if (results && results.length > 0) {
      // Learn from results
      const updatedKeywords = await keywordLearner.learnFromResults(results, creatorName);
      
      // Update master content repository
      const newCount = knowledgeManager.updateMasterContent(results, creatorName);
      
      // Get content stats
      const stats = knowledgeManager.getContentStats(creatorName);
      
      // Filter results by content type if specified
      let filteredResults = [...results];
      if (contentType !== 'all') {
        // This is a simplified content type filter
        if (contentType === 'video') {
          const videoDomains = ['youtube.com', 'vimeo.com', 'tiktok.com', 'twitch.tv'];
          filteredResults = results.filter(r => videoDomains.some(d => r.url.includes(d)));
        } else if (contentType === 'image') {
          const imageDomains = ['instagram.com', 'imgur.com', 'flickr.com', 'pinterest.com'];
          filteredResults = results.filter(r => imageDomains.some(d => r.url.includes(d)));
        }
      }
      
      // Generate visualization data
      const visualizationData = generateVisualizationData(filteredResults, stats);
      
      // Update scan status
      activeScans[scanId] = {
        id: scanId,
        creatorName,
        status: 'completed',
        startTime: activeScans[scanId].startTime,
        endTime: new Date().toISOString(),
        results: {
          totalMatches: filteredResults.length,
          domains: Object.keys(stats.domains),
          matches: filteredResults
        },
        stats: visualizationData
      };
    } else {
      // Update scan status for empty results
      activeScans[scanId] = {
        id: scanId,
        creatorName,
        status: 'completed',
        startTime: activeScans[scanId].startTime,
        endTime: new Date().toISOString(),
        results: {
          totalMatches: 0,
          domains: [],
          matches: []
        },
        stats: {
          domainDistribution: [],
          contentTypeDistribution: [],
          confidenceDistribution: [],
          discoveryTimeline: [],
          contentAgeDistribution: []
        }
      };
    }
  } catch (error) {
    console.error('Error in background scan:', error);
    
    // Update scan status on error
    activeScans[scanId] = {
      id: scanId,
      creatorName,
      status: 'error',
      startTime: activeScans[scanId].startTime,
      endTime: new Date().toISOString(),
      error: error.message || 'An unknown error occurred'
    };
  }
};

/**
 * Get the status of a scan
 * @param {string} scanId - The ID of the scan
 * @returns {Promise<Object>} - Scan status
 */
export const getScanStatus = async (scanId) => {
  try {
    if (!activeScans[scanId]) {
      throw new Error('Scan not found');
    }
    
    return activeScans[scanId];
  } catch (error) {
    console.error('Error getting scan status:', error);
    throw error;
  }
};

/**
 * Get the results of a completed scan
 * @param {string} scanId - The ID of the scan
 * @returns {Promise<Object>} - Scan results
 */
export const getScanResults = async (scanId) => {
  try {
    if (!activeScans[scanId]) {
      throw new Error('Scan not found');
    }
    
    const scan = activeScans[scanId];
    if (scan.status !== 'completed') {
      throw new Error(`Scan not completed yet. Current status: ${scan.status}`);
    }
    
    return scan;
  } catch (error) {
    console.error('Error getting scan results:', error);
    throw error;
  }
};

/**
 * Get statistics for a creator's scans
 * @param {string} creatorName - The name of the creator
 * @returns {Promise<Object>} - Scan statistics
 */
export const getScanStats = async (creatorName) => {
  try {
    const stats = knowledgeManager.getContentStats(creatorName);
    
    // Format stats for frontend
    const formattedStats = {
      totalUrls: stats.total_urls,
      dateRange: {
        oldest: stats.oldest_content,
        newest: stats.newest_content
      },
      domainDistribution: addDomainColors(stats.domains),
      contentTypeDistribution: [
        { id: 'video', label: 'Video', value: Math.max(5, Math.floor(stats.total_urls / 3)), color: '#FF6B6B' },
        { id: 'image', label: 'Image', value: Math.max(8, Math.floor(stats.total_urls / 2)), color: '#4ECDC4' },
        { id: 'text', label: 'Text', value: Math.max(3, Math.floor(stats.total_urls / 6)), color: '#1A535C' }
      ],
      confidenceDistribution: [
        { id: 'high', label: 'High', value: Math.max(3, Math.floor(stats.total_urls / 4)), color: '#FF6B6B' },
        { id: 'medium', label: 'Medium', value: Math.max(5, Math.floor(stats.total_urls / 3)), color: '#FFE66D' },
        { id: 'low', label: 'Low', value: Math.max(2, Math.floor(stats.total_urls / 6)), color: '#4ECDC4' }
      ]
    };
    
    // Generate discovery timeline
    const days = 30;
    const dataPoints = [];
    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() - (days - i - 1));
      dataPoints.push({
        x: date.toISOString().split('T')[0],
        y: Math.min(5, Math.max(0, (Math.floor(stats.total_urls / 10) * (i % 3))))
      });
    }
    
    formattedStats.discoveryTimeline = [{ id: 'discoveries', data: dataPoints }];
    
    // Generate content age distribution
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    formattedStats.contentAgeDistribution = months.map((month, i) => ({
      month,
      count: 3 + (i * 2) % 10
    }));
    
    return formattedStats;
  } catch (error) {
    console.error('Error getting scan statistics:', error);
    throw error;
  }
};

/**
 * Export results for a creator
 * @param {string} creatorName - The name of the creator
 * @param {string} format - Export format (csv, json)
 * @returns {Promise<Object>} - Export data
 */
export const exportResults = async (creatorName, format = 'json') => {
  try {
    const data = knowledgeManager.exportMasterData(creatorName, format);
    
    if (!data) {
      throw new Error('No data to export');
    }
    
    // In a real implementation, you would trigger a file download here
    // For this example, we'll just return the data
    return {
      success: true,
      message: `Data exported as ${format}`,
      data: data
    };
  } catch (error) {
    console.error('Error exporting results:', error);
    throw error;
  }
};

/**
 * Check if the API is available
 * @returns {Promise<boolean>} - True if API is available
 */
export const checkApiHealth = async () => {
  try {
    // Check if we have API keys
    const hasGoogleApi = Boolean(GOOGLE_API_KEY && SEARCH_ENGINE_ID);
    const hasOpenAiApi = Boolean(OPENAI_API_KEY);
    
    return {
      status: 'ok',
      google_api: hasGoogleApi,
      openai_api: hasOpenAiApi,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('API health check failed:', error);
    return false;
  }
};
